<template>
  <v-dialog
    v-model="manage_modal"
    width=75%
    outline
    persistent
  >
    <v-card>
      <v-card-title class="text-h5" primary-title>
        <span>Manage Facility Reservation</span>
      </v-card-title>
      <v-divider />
      <v-layout v-if="noteLoading" justify-center>
        <v-progress-circular
          :size="60"
          :width="6"
          indeterminate
          class="my-3"
        />
      </v-layout>
      <v-card-text class="pa-3" v-else>
        <v-layout class="subheading" column>
          <v-tabs v-model="activeTab">
            <v-tab
              data-testid="manage-reservation-details-tab"
              class="manage-reservation-details-tab"
              v-if="!needsUpdate"
              @click="state = 'select'"
            >
              Reservation Details
            </v-tab>
            <v-tab
              data-testid="manage-reservation-update-tab"
              class="manage-reservation-update-tab"
              v-if="!needsUpdate"
              @click="state = 'updateReservation'"
            >
              Update Reservation
            </v-tab>
            <v-tab
              data-testid="manage-reservation-add-note-tab"
              class="manage-reservation-add-note-tab"
              v-if="!needsUpdate"
              @click="state = 'note'"
            >
              Add Note
            </v-tab>
            <v-tab
              data-testid="manage-reservation-get-units-tab"
              class="manage-reservation-get-units-tab"
              @click="getUnits"
            >
              <span v-if="!needsUpdate">Switch Unit Size</span>
              <span v-else>Update Reservation</span>
            </v-tab>
            <v-tab-item class="mt-3" eager>
              <span class="text-h6 ma-2">Reservation Details</span>
              <v-layout>
                <v-layout column="mx-2 mt-2">
                  <v-layout class="mx-2 mt-3">
                    <v-flex xs4>Reservation Type:</v-flex>
                    <v-flex xs8>
                      <span v-if="reservation_type == 1">Lead</span>
                      <span v-else>Reservation</span>
                    </v-flex>
                  </v-layout>
                  <v-layout class="mx-2 mt-3">
                    <v-flex xs4>Quoted Price:</v-flex>
                    <v-flex xs8>${{ parseFloat(reservation_quoted_rate).toFixed(2) }}</v-flex>
                  </v-layout>
                  <v-layout class="mx-2 mt-3">
                    <v-flex xs4>Move-In Date:</v-flex>
                    <v-flex xs8>{{ reservation_move_in_date ? formatDateUTC(reservation_move_in_date) : "—" }}</v-flex>
                  </v-layout>
                  <v-layout class="mx-2 mt-3">
                    <v-flex xs4>Next Follow-up:</v-flex>
                    <v-flex xs8>{{ reservation_follow_up_date ? formatDate(reservation_follow_up_date) : "—" }}</v-flex>
                  </v-layout>
                  <v-layout class="mx-2 mt-3">
                    <v-flex xs4>Last Contacted:</v-flex>
                    <v-flex xs8>
                      <span v-if="reservation.should_followup == 'true'">
                        {{ formatDateAndTime(reservation.updated_at || reservation.last_followup_at) }}
                      </span>
                      <span v-else>Has not been contacted.</span>
                    </v-flex>
                  </v-layout>
                  <v-layout class="mx-2 mt-3">
                    <v-flex xs4>Promotion Offered:</v-flex>
                    <v-flex xs8>{{ promotionOptions && promotionOptions.this_month && promotionOptions.this_month.text || "None" }}</v-flex>
                  </v-layout>
                </v-layout>
                <v-layout column="mx-2 mt-2">
                  <v-layout class="mx-2 mt-3">
                    <v-flex xs4>Unit Type:</v-flex>
                    <v-flex xs8>{{ unitFeatures.product_type }}</v-flex>
                  </v-layout>
                  <v-layout class="mx-2 mt-3">
                    <v-flex xs4>Unit Size:</v-flex>
                    <v-flex xs8>
                      <span v-if="parseFloat(unitFeatures.width)">
                        '{{ parseFloat(unitFeatures.width) }} x '{{ parseFloat(unitFeatures.length) }}
                      </span>
                      <span v-else>'{{ parseFloat(unitFeatures.length )}}</span>
                    </v-flex>
                  </v-layout>
                  <v-layout class="mx-2 mt-3">
                    <v-flex xs4>Features:</v-flex>
                    <v-layout column>
                      <v-flex
                        xs8
                        v-for="(feature, i) of unitFeatures.features"
                        :key="i"
                      >
                        {{ feature }}
                      </v-flex>
                    </v-layout>
                  </v-layout>
                  <v-layout class="mx-2 mt-3">
                    <v-flex xs4>Facility Phone Number:</v-flex>
                    <v-flex xs8>{{ addDashes(facility.phone) }}</v-flex>
                  </v-layout>
                </v-layout>
              </v-layout>
              <v-layout class="mx-2 my-3">
                <v-flex xs2>Reservation Notes:</v-flex>
                <v-flex xs10>
                  <v-layout
                      v-if="notes.length"
                      column
                      style="max-height: 250px; outline: 1px grey solid; overflow-y:auto;"
                      class="px-3 pt-2"
                      >
                        <v-flex v-for="(note, i) of sortedNotes" :key="i">
                          <v-layout column class="mb-3 subheading">
                            {{ note.description }}
                            <v-layout class="mt-1" align-center>
                              <v-icon class="mr-1" small>create</v-icon>
                              <span class="text-caption">Created on {{ formatDate(note.created_at) }}</span>
                            </v-layout>
                          </v-layout>
                        </v-flex>
                  </v-layout>
                  <span v-else class="ml-3">There are no notes to display for this reservation.</span>
                </v-flex>
              </v-layout>
              <v-layout
                class="mx-2 my-3"
                v-if="moveIn"
                column
              >
                <v-layout
                  v-if="invalidPromotion && options.selectedPromotion === null"
                >
                  <v-flex xs2></v-flex>
                  <v-flex xs10 class="pa-4 xs12 my-4"
                  style="border: 1px solid red;">
                    <v-icon color="red" dense>warning</v-icon>
                    <span class="ml-3">The promotion associated with this reservation is no longer active.
                    The move-in cost displayed does not include a promotion. You will be given the option to select a new promotion when converting this reservation.</span>
                  </v-flex>
                </v-layout>
                <v-layout>
                  <v-flex xs2>Move-In Cost:</v-flex>
                  <v-flex xs10>
                    <v-layout
                      v-if="moveIn"
                      column
                      style="max-height: 250px; outline: 1px grey solid; overflow-y:auto;"
                      class="px-3 pt-2"
                    >
                      <v-flex v-for="(fee, i) of moveIn.fees" :key="`mi${i}`">
                        <v-layout class="pb-1">
                          <v-flex xs6>
                            {{ fee.name }}
                          </v-flex>
                          <v-flex xs6>
                            ${{ fee.cost }}
                          </v-flex>
                        </v-layout>
                      </v-flex>
                      <v-divider class="mx-1 my-2"></v-divider>
                      <v-layout class="subheading font-weight-bold pb-3">
                        <v-flex xs6>
                          Non-refundable Total Charges:
                        </v-flex>
                        <v-flex xs6>
                          ${{ moveIn.total }}
                        </v-flex>
                      </v-layout>
                    </v-layout>
                  </v-flex>
                </v-layout>
              </v-layout>
            </v-tab-item>
            <v-tab-item class="mt-3" eager>
              <span class="text-h6 mx-2">Update Reservation Details</span>
              <validation-observer ref="observer" v-slot="{ invalid }">
              <v-form ref="form" class="my-3">
                <v-layout column>
                  <v-layout class="ma-2 pb-2" align-center>
                    <v-flex xs4>
                      <v-layout>
                        <v-flex xs4>Tenant Name:</v-flex>
                        <v-flex>
                          {{ reservation.customer_first_name || "" }} {{reservation.customer_last_name || ""}}
                        </v-flex>
                      </v-layout>
                    </v-flex>
                    <v-flex xs4>
                      <v-layout align-center>
                        <v-flex xs4>Quoted Price:</v-flex>
                        <v-flex>
                          <app-input
                            data-testid="manage-reservation-quoted-price-input"
                            class="manage-reservation-quoted-price-input pr-2"
                            v-model="reservation_quoted_rate"
                            v-if="canEdit"
                            solo
                            style="max-width: 10rem"
                          ></app-input>
                          <span v-else>${{ parseFloat(reservation_quoted_rate).toFixed(2) }}</span>
                        </v-flex>
                      </v-layout>
                    </v-flex>
                    <v-flex xs4>
                      <v-layout>
                        <v-flex class="ml-2">
                          <v-radio-group
                            box
                            row
                            label="Label:"
                            v-model="reservation_type"
                            hide-details
                          >
                            <template v-slot:label>
                              <div class="white--text mr-3">Reservation Type:</div>
                            </template>
                            <v-radio
                              data-testid="manage-reservation-type-lead-radio"
                              class="manage-reservation-type-lead-radio"
                              label="Lead"
                              :value="1"
                            />
                            <v-radio
                              data-testid="manage-reservation-type-reservation-radio"
                              class="manage-reservation-type-reservation-radio"
                              label="Reservation"
                              :value="2"
                            />
                          </v-radio-group>
                        </v-flex>
                      </v-layout>
                    </v-flex>
                  </v-layout>
                  <v-layout class="mx-2">
                    <v-flex xs4>
                      <v-select
                        data-testid="manage-reservation-status-option-menu"
                        class="manage-reservation-status-option-menu mr-3"
                        v-model="status"
                        filled
                        :items="statusOptions"
                        item-text="text"
                        label="Status"
                      />
                    </v-flex>
                    <v-flex xs4>
                      <v-menu
                        data-testid="manage-reservation-move-in-menu"
                        class="manage-reservation-move-in-menu"
                        ref="moveIn"
                        v-model="moveInSelect"
                        :close-on-content-click="false"
                        :return-value.sync="moveInDate"
                        :nudge-right="10"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on }">
                          <app-input
                            data-testid="manage-reservation-move-in-date-input"
                            class="manage-reservation-move-in-date-input px-1 mx-1"
                            v-model="moveInDate"
                            filled
                            prepend-icon="event"
                            label="Move-in Date"
                            :rules="'required'"
                            readonly
                            v-on="on"
                          ></app-input>
                        </template>
                        <v-date-picker
                          data-testid="manage-reservation-move-in-date-picker"
                          class="manage-reservation-move-in-date-picker"
                          v-model="moveInDate"
                          no-title
                          scrollable
                          :min="today"
                          :max="moveInMax"
                          @change="$refs.moveIn.save(moveInDate)"
                        />
                      </v-menu>
                    </v-flex>
                    <v-flex xs4>
                      <v-menu
                        data-testid="manage-reservation-follow-up-menu"
                        class="manage-reservation-follow-up-menu"
                        v-model="followUpSelect"
                        ref="followUp"
                        :return-value.sync="followupDate"
                        :nudge-right="10"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on }">
                          <app-input
                            data-testid="manage-reservation-follow-up-date-input"
                            class="manage-reservation-follow-up-date-input px-1 mx-1"
                            v-model="followupDate"
                            filled
                            prepend-icon="event"
                            label="Follow-up Date"
                            :rules="'required'"
                            readonly
                            v-on="on"
                          ></app-input>
                        </template>
                        <v-date-picker
                          data-testid="manage-reservation-follow-up-date-picker"
                          class="manage-reservation-follow-up-date-picker"
                          v-model="followupDate"
                          no-title scrollable
                          :min="today"
                          :max="followUpMax"
                          @change="$refs.followUp.save(followupDate)"
                        />
                      </v-menu>
                    </v-flex>
                  </v-layout>
                  <v-layout justify-end>
                    <v-btn
                      data-testid="manage-reservation-update-btn"
                      class="manage-reservation-update-btn"
                      color="primary"
                      :loading="updateReservationLoading"
                      :disabled="updateTenantLoading || cancelReservationLoading || invalid"
                      @click="updateReservation"
                    >
                      Update Reservation
                    </v-btn>
                  </v-layout>
                </v-layout>
              </v-form>
              </validation-observer>
              <v-divider class="mt-3 mb-3" />
              <v-layout class="ml-2 mt-4" justify-space-between align-center>
                <v-flex class="text-h6">Update Tenant Information</v-flex>
              </v-layout>
              <validation-observer ref="observer" v-slot="{ invalid }">
              <v-form ref="tenantInfoForm" class="my-3">
                <v-layout column>
                  <v-layout>
                    <app-input
                      data-testid="manage-reservation-first-name-input"
                      class="manage-reservation-first-name-input px-2"
                      v-model="updateTenantPayload.first_name"
                      filled
                      label="First Name"
                      :rules="'required'"
                    ></app-input>
                    <app-input
                      data-testid="manage-reservation-last-name-input"
                      class="manage-reservation-last-name-input px-2"
                      v-model="updateTenantPayload.last_name"
                      filled
                      label="Last Name"
                      :rules="'required'"
                    ></app-input>
                    <app-input
                      data-testid="manage-reservation-phone-input"
                      class="manage-reservation-phone-input px-2"
                      filled
                      label="Phone"
                      type="phone"
                      v-model="updateTenantPayload.phone"
                      :rules="'required|min:10'"
                    ></app-input>
                    <app-input
                      data-testid="manage-reservation-email-input"
                      class="manage-reservation-email-input px-2"
                      filled
                      label="Email"
                      type="email"
                      v-model="updateTenantPayload.email"
                      :rules="'required|email'"
                    ></app-input>
                  </v-layout>
                </v-layout>
                <v-layout justify-end>
                  <v-btn
                    data-testid="manage-reservation-update-tenant-btn"
                    class="manage-reservation-update-tenant-btn"
                    color="primary"
                    :loading="updateTenantLoading"
                    :disabled="updateReservationLoading || invalid"
                    @click="updateTenant"
                  >
                    Update Tenant
                  </v-btn>
                </v-layout>
              </v-form>
              </validation-observer>
              <v-divider class="mt-3 mb-3" />
              <v-layout column v-show="userIsAdmin || userIsSalesAgent">
                <v-flex class="text-h6 ml-2 mt-4">Cancel Reservation</v-flex>
                <v-layout />
                <v-layout justify-end>
                  <v-btn
                    data-testid="manage-reservation-cancel-btn"
                    class="manage-reservation-cancel-btn"
                    color="error"
                    :loading="cancelReservationLoading"
                    :disabled="updateReservationLoading || updateTenantLoading"
                    @click="updateReservation(2)"
                  >
                    Cancel Reservation
                  </v-btn>
                </v-layout>
              </v-layout>
            </v-tab-item>
            <v-tab-item class="my-3" eager>
              <span class="text-h6 mx-2 mt-4">Add Note</span>
              <v-form>
                <v-textarea
                  data-testid="manage-reservation-add-note-textarea"
                  class="manage-reservation-add-note-textarea px-2 pt-3"
                  v-model="note"
                  filled
                  label="Add note..."
                  hint="When you are finished, click 'Add Note' to save this note to this tenant's account."
                />
              </v-form>
            </v-tab-item>
            <v-tab-item>
              <v-layout v-if="setUnitLoading" align-center justify-center>
                <v-progress-circular
                  :size="60"
                  :width="6"
                  indeterminate
                  class="my-3"
                />
              </v-layout>
              <v-layout column v-else>
                <v-layout justify-space-between>
                  <v-layout column class="text-h6 mx-2 mt-4">
                    <v-flex>
                      <span v-if="!needsUpdate">Switch Unit Size</span>
                      <span v-else>Update Reservation</span>
                    </v-flex>
                    <v-flex class="text-body-1" v-if="!needsUpdate">Current unit size:
                      <span v-if="parseFloat(unitFeatures.width)">
                        '{{ parseFloat(unitFeatures.width) }} x '{{ parseFloat(unitFeatures.length) }}
                      </span>
                      <span v-else>'{{ parseFloat(unitFeatures.length )}}</span>
                    </v-flex>
                  </v-layout>
                  <v-autocomplete
                      data-testid="manage-reservation-switch-unit-menu"
                      class="manage-reservation-switch-unit-menu pb-2"
                      v-model="selectedFeature"
                      align-with-title
                      :items="availableFeatures"
                      label="Filter by available features..."
                      :hint="!selectedFeature ? 'Enter or select a feature' : 'Click X to reset the list'"
                      persistent-hint
                      clearable
                      style="max-width: 25%"
                  >
                    <template slot="item" slot-scope="data">
                      <v-list-item-content>
                        {{ data.item }}
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </v-layout>
                <v-data-table
                  :headers="headers"
                  :loading="loading"
                  :items="filteredUnits"
                  :options.sync="pagination"
                  no-data-text="There are no units to display"
                >
                  <template slot="item" slot-scope="props">
                    <tr>
                    <td class="py-2">
                      <div class="text-body-2 font-weight-bold">
                        <span v-if="props.item.width">
                          {{ props.item.width }}' x {{ props.item.length }}' - {{ props.item.type_name }}
                        </span>
                        <span v-else>{{ props.item.length }}' - {{ props.item.type_name }}</span>
                      </div>
                      <span class="text-caption">
                        {{ props.item.features.join(", ") }}
                      </span>
                    </td>
                    <td class="py-1">
                      <span v-if="props.item.width">{{ props.item.area }} sq. ft.</span>
                      <span v-else>N/A</span>
                    </td>
                    <td class="py-1">{{ props.item.promotion_text }}</td>
                    <td class="py-1">${{ props.item.rate.toFixed(2) }}</td>
                    <td class="py-1">{{ props.item.product_type }}</td>
                    <td class="py-1">{{ props.item.total_vacant }}</td>
                    <td class="py-1">
                      <v-layout column>
                        <v-btn
                          data-testid="manage-reservation-select-unit-btn"
                          class="manage-reservation-select-unit-btn primary"
                          small
                          @click.stop="setUnit(props.item)"
                        >
                          Select
                        </v-btn>
                      </v-layout>
                    </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-layout>
            </v-tab-item>
          </v-tabs>
        </v-layout>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-btn
          data-testid="manage-reservation-dismiss-modal-btn"
          class="manage-reservation-dismiss-modal-btn"
          color="error"
          @click="closeModal"
        >
          Dismiss
        </v-btn>
        <v-spacer />
        <v-btn
          data-testid="manage-reservation-get-move-in-btn"
          class="manage-reservation-get-move-in-btn"
          v-show="activeTab === 0 && !moveIn"
          color="primary"
          :loading="moveInCostLoading"
          @click="getMoveInCost"
        >
          Get Move-In Cost
        </v-btn>
        <v-btn
          data-testid="manage-reservation-add-note-btn"
          class="manage-reservation-add-note-btn"
          v-show="activeTab === 2"
          color="primary"
          :loading="noteLoading"
          :disabled="!note"
          @click="addNote"
        >
          Add Note
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/* eslint-disable eqeqeq */
/* eslint-disable no-return-assign */
/* eslint-disable no-undef */
/* eslint-disable no-unused-expressions */
import { ValidationObserver } from "vee-validate";
import { dateMethodMixins } from "@/mixins/dateMethodMixins";
import { manageReservationHeaders } from "@/helpers/manage-reservations/manage-reservations-data-table-headers";
import { mapState } from "vuex";
import moment from "moment";
import states from "@/helpers/states";

export default {
  props: [
    "facility",
    "reservation",
    "manageModal",
    "unitFeatures"
  ],

  mixins: [dateMethodMixins],

  data() {
    return {
      loading: false,
      cancelReservationLoading: false,
      updateReservationLoading: false,
      updateTenantLoading: false,
      noteLoading: true,
      infoLoading: false,
      setUnitLoading: false,
      needsUpdate: false,
      messagePayload: "",

      dialog: false,

      state: "select",

      status: 0,
      activeTab: 0,

      availableUnits: [],
      selectedFeature: "",
      followUp: "",
      moveInSelect: null,
      followUpSelect: null,

      note: "",
      notes: [],

      states: states,

      updateTenantPayload: {
        first_name: this.reservation.customer_first_name || "",
        last_name: this.reservation.customer_last_name || "",
        phone: this.reservation.customer_phone || "",
        email: this.reservation.customer_email || ""
      },

      headers: manageReservationHeaders,

      statusOptions: [
        {
          text: "Do not update status",
          value: 0
        },
        {
          text: "Restore this reservation",
          value: 1
        }
      ],

      pagination: {
        sortBy: ["area"],
        sortDesc: [true],
        itemsPerPage: -1
      },

      reservation_quoted_rate: this.reservation.quoted_price ? this.reservation.quoted_price : null,
      reservation_type: this.reservation.type ? this.reservation.type : null,
      reservation_move_in_date: this.reservation.move_in_date ? this.reservation.move_in_date : null,
      reservation_follow_up_date: this.reservation.followup_at ? this.reservation.followup_at : null,
      manage_modal: this.manageModal
    };
  },

  methods: {
    closeModal() {
      this.state = "select";
      this.status = 0;
      this.$emit("collapse");
    },

    getNotes() {
      this.noteLoading = true;
      let payload = {
        facility_id: this.$route.params.facilityId,
        waiting_list_id: this.reservation.waiting_list_id
      };

      this.$store.dispatch("getNotes", payload)
        .then(resp => {
          this.noteLoading = false;
          this.notes = resp.data;
        })
        .catch(err => {
          this.noteLoading = false;
          this.$store.dispatch("handleError", err);
        });
    },

    addNote() {
      this.noteLoading = true;

      let payload = {
        facility_id: this.$route.params.facilityId,
        waiting_list_id: this.reservation.waiting_list_id,
        description: this.note
      };

      this.$store.dispatch("addNote", payload)
        .then(resp => {
          this.noteLoading = false;
          this.$store.dispatch("handleSuccess", "Reservation note added!");
          this.notes = resp.data;
          this.state = "select";
          this.note = "";
        })
        .catch(err => {
          this.noteLoading = false;
          this.$store.dispatch("handleError", err);
        });
    },

    getFacilityInfo(tenant) {
      this.routeLoading = true;
      this.$store.dispatch("getFacility", tenant.sLocationCode)
        .then(resp => {
          router.push({
            name: "tenantDetails",
            params: {
              tenantId: tenant.tenant_id,
              facilityId: resp.data.facility.id
            }
          }).catch(() => {});
          this.$store.commit("changingTenant", true);
          this.routeLoading = false;
          this.collapse();
        })
        .catch(err => {
          this.routeLoading = false;
          this.$store.dispatch("handleError", err);
        });
    },

    getUnits() {
      this.infoLoading = true;
      this.$store.dispatch("getFacility", this.$route.params.facilityId)
        .then(resp => {
          let facility = resp.data.facility;
          this.availableUnits = facility.units.parking ? facility.units.storage.concat(facility.units.parking) : facility.units.storage;
          this.state = "updateUnit";
          this.infoLoading = false;
        })
        .catch(err => {
          this.availableUnits = [];
          this.$store.dispatch("handleError", err);
          this.infoLoading = false;
        });
    },

    updateReservation(cancelEvent) {
      let payload = {
        waitingListId: this.reservation.waiting_list_id,
        facilityId: this.$route.params.facilityId,
        tenant_id: parseFloat(this.reservation.tenant_id),
        status: "",
        is_follow_up: !!this.reservation.should_followup,
        move_in_date: moment.utc(this.moveInDate).format("MM/DD/YYYY"),
        followup_date: moment.utc(this.followupDate).format("MM/DD/YYYY"),
        // we don't care about inquiry_type in our code, and don't want to hardcode a map between codes/values on the FE. 3 == "Phone"
        inquiry_type: 3,
        quoted_rate: +parseFloat(this.reservation_quoted_rate).toFixed(2),
        unit_id: +this.reservation.unit_id,
        reservation_type: +this.reservation_type,
        promotion_id: this.reservation.concession_id ? this.reservation.concession_id : null
      };

      if (cancelEvent === 2) {
        this.cancelReservationLoading = true;
        payload.status = cancelEvent;
        this.messagePayload = "Reservation cancelled successfully!";
      } else {
        this.updateReservationLoading = true;
        payload.status = this.status;
        this.messagePayload = "Reservation updated successfully!";
      }

      this.$store.dispatch("updateReservation", payload)
        .then(() => {
          this.$store.dispatch("handleSuccess", this.messagePayload);
          this.$emit("reservationUpdated", payload);
          payload.status = "";
          this.$emit("collapse");
          this.cancelReservationLoading = false;
          this.updateReservationLoading = false;
        })
        .catch(err => {
          this.cancelReservationLoading = false;
          this.updateReservationLoading = false;
          this.$store.dispatch("handleError", err);
        });
    },

    updateTenant() {
      let payload = {
        facilityId: this.$route.params.facilityId,
        waitingListId: this.reservation.waiting_list_id,
        move_in_date: moment(this.moveInDate).format("MM/DD/YYYY"),
        tenant_id: parseFloat(this.reservation.tenant_id),
        status: this.status,
        is_follow_up: !!this.reservation.should_followup,
        followup_date: moment.utc(this.followupDate).format("MM/DD/YYYY"),
        follow_up_date_last: moment.utc().format("MM/DD/YYYY"),
        inquiry_type: 3,
        quoted_rate: +parseFloat(this.reservation_quoted_rate).toFixed(2),
        reservation_type: +this.reservation_type,
        unit_id: +this.reservation.unit_id,
        promotion_id: this.reservation.concession_id ? this.reservation.concession_id : null,
        ...this.updateTenantPayload
      };

      this.updateTenantLoading = true;
      this.$store.dispatch("updateReservation", payload)
        .then(() => {
          this.$emit("collapse");
          this.updateTenantLoading = false;
          this.$store.dispatch("handleSuccess", "Tenant information successfully updated!");
          this.$emit("tenantUpdated");
          this.$emit("reservationUpdated", payload);
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.updateTenantLoading = false;
        });
    },

    getUnitInfo() {
      this.$store.dispatch("getFacility", this.$route.params.facilityId)
        .then(resp => {
          if (this.reservation.unit_id < 10000) {
            this.unit = resp.data.facility.units.storage.filter(unit => unit.unit_id_first_available == this.reservation.unit_id)[0];
          } else {
            this.unit = resp.data.facility.units.parking.filter(unit => unit.unit_id_first_available == this.reservation.unit_id)[0];
          };
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
        });
    },

    getMoveInCost() {
      let payload = {
        external_unit_id: this.reservation.unit_id,
        external_promotion_id: this.reservation.concession_id ? this.reservation.concession_id : null,
        move_in_date: moment.utc(this.reservation_move_in_date).format("MM/DD/YYYY"),
        external_reservation_id: this.reservation.waiting_list_id ? this.reservation.waiting_list_id : null,
        facilityId: this.$route.params.facilityId
      };
      this.$store.dispatch("getMoveInCost", payload)
        .catch(() => {
          payload.external_promotion_id = "0";
          this.$store.dispatch("getMoveInCost", payload)
            .catch(err => {
              this.$store.dispatch("handleError", err);
            });
        });
    },

    setUnit(unit) {
      this.setUnitLoading = true;
      this.updateReservationLoading = true;

      let payload = {
        facilityId: this.$route.params.facilityId,
        waitingListId: this.reservation.waiting_list_id,
        move_in_date: moment(this.moveInDate).format("MM/DD/YYYY"),
        tenant_id: parseFloat(this.reservation.tenant_id),
        status: this.status,
        is_follow_up: !!this.reservation.should_followup,
        followup_date: moment.utc(this.followupDate).format("MM/DD/YYYY"),
        follow_up_date_last: moment.utc().format("MM/DD/YYYY"),
        inquiry_type: 3,
        quoted_rate: +parseFloat(unit.rate).toFixed(2),
        reservation_type: +this.reservation_type,
        unit_id: +unit.unit_id_first_available,
        unit_dimensions: unit.width + " x " + unit.length,
        promotion_id: unit.concession_id
      };

      this.$store.dispatch("updateReservation", payload)
        .then(resp => {
          this.setUnitLoading = false;
          this.updateReservationLoading = false;
          this.$store.dispatch("handleSuccess", "Reservation updated successfully!");
          this.$emit("reservationUpdated", payload);
          this.$emit("collapse");
        })
        .catch(err => {
          this.setUnitLoading = false;
          this.updateReservationLoading = false;
          this.$store.dispatch("handleError", err);
        });
    },

    timestamp(date) {
      date = new Date(date);
      return moment.utc(date).local().format("MM-DD-YYYY hh:mm a");
    },

    allowedMoveInDates(val) {
      const earliest = moment();
      const latest = moment().add(60, "days");
      return val > earliest && val <= latest;
    },

    addDashes(phone) {
      if (phone) {
        if (phone.length === 10 && typeof phone.replace === "function") {
          return phone.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
        } else {
          return phone;
        }
      }
    },

    sortNotes(notes) {
      return notes.sort((a, b) => {
        let date1 = new Date(a.created_at).getTime();
        let date2 = new Date(b.created_at).getTime();
        return date2 - date1;
      });
    }
  },

  computed: {
    ...mapState({
      moveIn: state => state.facilities.moveInCost,
      moveInCostLoading: state => state.facilities.moveInCostLoading,
      userIsAdmin: state => state.user.isAdmin,
      userIsSalesAgent: state => state.user.me.roles.includes("call-center-agent"),
      promotionOptions: state => state.facilities.promotionOptions,
      invalidPromotion: state => state.reservations.invalidPromotion,
      options: state => state.tenant.options
    }),

    canEdit() {
      return (this.$store.state.user.me.roles.indexOf("call-center-agent") > -1);
    },

    today() {
      let now = new Date();
      return new moment(now).format("YYYY-MM-DD");
    },

    moveInMax() {
      let date = new Date();
      return new moment(date).add(21, "days").format("YYYY-MM-DD");
    },

    followUpMax() {
      let date = new Date();
      return new moment(date).add(21, "days").format("YYYY-MM-DD");
    },

    moveInDate: {
      get() {
        return this.reservation_move_in_date ? moment.utc(this.reservation_move_in_date).format("YYYY-MM-DD") : "";
      },
      set(newVal) {
        this.reservation_move_in_date = newVal;
      }
    },

    followupDate: {
      get() {
        return this.reservation_follow_up_date ? moment(this.reservation_follow_up_date).format("YYYY-MM-DD") : "";
      },
      set(newVal) {
        this.reservation_follow_up_date = newVal;
      }
    },

    sortedNotes() {
      return this.sortNotes(this.notes);
    },

    filteredUnits() {
      return this.selectedFeature ? this.availableUnits.filter(unit => {
        for (let detail in unit) {
          if (detail === "features" && Object.values(unit[detail]).includes(this.selectedFeature)) {
            return unit[detail];
          }
        }
      }) : this.availableUnits;
    },

    availableFeatures() {
      let arr = [];
      this.availableUnits.map(unit => {
        for (let detail in unit) {
          if (detail === "features") {
            Object.values(unit[detail]).forEach(value => arr.push(value));
          }
        }
      });
      return [...new Set(arr)];
    }
  },

  watch: {
    manage_modal() {
      if (this.manage_modal) {
        this.activeTab = 0;
      }
    },
    reservation() {
      // @todo - reservation data
      this.updateTenantPayload.first_name = this.reservation.customer_first_name || "";
      this.updateTenantPayload.last_name = this.reservation.customer_last_name || "";
      this.updateTenantPayload.phone = this.reservation.customer_phone || "";
      this.updateTenantPayload.email = this.reservation.customer_email || "";
      this.reservation_quoted_rate = this.reservation.quoted_price ? this.reservation.quoted_price : null;
      this.reservation_type = this.reservation.type ? this.reservation.type : null;
      this.reservation_move_in_date = this.reservation.move_in_date ? this.reservation.move_in_date : null;
      this.reservation_follow_up_date = this.reservation.followup_at ? this.reservation.followup_at : null;
      this.manage_modal = this.manageModal;
      if (this.manage_modal && !this.reservation.unit_id) {
        // if no id, force agent to select new unit
        this.needsUpdate = true;
        this.activeTab = 3;
        this.getUnits();
        this.$store.dispatch("handleAlert", "We cannot locate the original reservation for this customer. " +
                    "Please inform the customer and use the provided list to select an available unit.");
      }
    },
    manageModal(val) {
      if (val) {
        this.getNotes();
      }
    }
  },

  components: {
    ValidationObserver
  }

};
</script>
